import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import {
    BsBriefcase, BsCashCoin, BsFileEarmarkText, BsHeartPulse, BsPersonVcard,
    BsTelephone, BsGeoAlt, BsClockHistory, BsPencilSquare, BsKey, BsPersonBadge,
} from "react-icons/bs";
import Handler from "../../Helpers/Handler";
import { currencyFormatter, formatDate, formatDateTime, textCapitalize } from "../../Helpers/Utils";
import { DetailCard, Skeleton } from "../../Components/Needed";
import StaffRoleModal from "./StaffRoleModal";
import StaffPasswordModal from "./StaffPasswordModal";
import StaffLocationCard from "./StaffLocationCard";
import Timeline from "../../Components/Timeline";
import { listBackPath } from "../../Helpers/useListParams";

const formatPreviousValue = (value) => {
    if (value === undefined) return null;
    if (value === null || value === "") return "empty";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "empty";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

const formatAddress = (address) => {
    if (!address) return null;
    const parts = [address.street, address.city, address.state, address.postalCode, address.country].filter(Boolean);
    return parts.length ? parts.join(", ") : null;
};

const initials = (name) => {
    const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "?";
    return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

// Human "2y 3m" / "8m" / "12d" tenure string, or null before dateOfJoining.
const tenureSince = (date) => {
    if (!date) return null;
    const start = new Date(date);
    if (Number.isNaN(start.getTime())) return null;
    const now = new Date();
    if (start > now) return null;
    let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
    if (now.getDate() < start.getDate()) months -= 1;
    if (months < 1) {
        const days = Math.max(1, Math.floor((now - start) / (1000 * 60 * 60 * 24)));
        return `${days}d`;
    }
    const years = Math.floor(months / 12);
    const remMonths = months % 12;
    if (years < 1) return `${months}m`;
    return remMonths ? `${years}y ${remMonths}m` : `${years}y`;
};

const EMPLOYMENT_STATUS_BADGE = {
    active: "text-bg-success",
    suspended: "text-bg-warning",
    terminated: "text-bg-danger",
};

// Grouped label/value grid used across the profile sections below - replaces
// the old flat 25-row field-row stack with scannable two-column cards, each
// value stacked under its label instead of pinned right, which reads far
// better once labels/values get longer (addresses, certifications, etc).
const InfoItem = ({ label, value }) => (
    <div className="staff-info-item">
        <span className="staff-info-label">{label}</span>
        <span className="staff-info-value">{value || value === 0 ? value : <span className="text-muted">Not set</span>}</span>
    </div>
);

const InfoSection = ({ icon, title, children }) => (
    <div className="staff-info-section">
        {title ? (
            <h6 className="staff-info-section-title">
                {icon ? <span className="staff-info-section-icon">{icon}</span> : null} {title}
            </h6>
        ) : null}
        <div className="staff-info-grid">{children}</div>
    </div>
);

const Chip = ({ children }) => (children ? <span className="staff-chip">{children}</span> : null);

const StaffDetail = () => {
    const { staffId } = useParams();
    const navigate = useNavigate();
    const [staff, setStaff] = useState(null);
    const [logs, setLogs] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [roleDepartmentId, setRoleDepartmentId] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        const [staffResponse, logResponse, departmentsResponse] = await Promise.all([
            Handler({ method: "GET", url: `/admin/staff/${staffId}` }),
            Handler({ method: "GET", url: `/admin/staff/${staffId}/log`, params: { limit: 20 } }),
            Handler({ method: "GET", url: "/admin/staff/department/active" }),
        ]);
        const staffData = staffResponse?.success ? staffResponse.data.data : null;
        setStaff(staffData);
        setLogs(logResponse?.success ? logResponse?.data?.data || [] : []);
        setDepartments(departmentsResponse?.success ? departmentsResponse?.data?.data || [] : []);
        if (staffData?.roleId) {
            const roleResponse = await Handler({ method: "GET", url: `/admin/staff/role/${staffData.roleId}` });
            setRoleDepartmentId(roleResponse?.success ? roleResponse?.data?.data?.departmentId || "" : "");
        }
        setLoading(false);
    }, [staffId]);

    useEffect(() => { load(); }, [load]);

    const changeStatus = async (employmentStatus) => {
        if (employmentStatus === staff?.employmentStatus) return;
        setStatusUpdating(true);
        const response = await Handler({ method: "patch", url: `/admin/staff/${staffId}/status`, data: { employmentStatus } });
        if (response?.success) {
            toast.success(response?.data?.message || "Status updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update status"));
        }
        setStatusUpdating(false);
    };

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={80} className="mb-3" />
                <Skeleton height={24} width="30%" className="mb-3" />
                <Skeleton height={220} />
            </div>
        );
    }

    if (!staff) {
        return (
            <div className="settings-card text-center py-5">
                <p className="text-muted mb-3">Staff member not found.</p>
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(listBackPath("/staff"))}>Back to staff</button>
            </div>
        );
    }

    const isTerminated = staff.employmentStatus === "terminated";
    const hasFinancialInfo = staff.baseSalary !== undefined || staff.allowances !== undefined || staff.bankDetails || staff.uanNumber || staff.pfNumber || staff.esiNumber;
    const hasEmergencyContact = staff.emergencyContact && Object.values(staff.emergencyContact).some(Boolean);
    const hasAddress = formatAddress(staff.currentAddress) || formatAddress(staff.permanentAddress);
    const hasCompliance = staff.aadhaarNumber || staff.panNumber || staff.drivingLicenseNumber;
    const hasEducation = staff.highestEducation || staff.certifications?.length || staff.languagesKnown?.length;
    const tenure = tenureSince(staff.dateOfJoining);

    return (
        <>
            <button type="button" className="btn btn-link text-decoration-none px-3 mb-3 d-inline-flex align-items-center gap-2" onClick={() => navigate(listBackPath("/staff"))}>
                <FaArrowLeft /> Back to staff
            </button>

            {/* Profile header - identity, status, and the four actions that used
                to live buried in a field row now read at a glance. */}
            <div className="card border-0 shadow-sm rounded-4 mb-4 staff-header-card">
                <div className="card-body d-flex flex-wrap align-items-center gap-3">
                    <div className="staff-avatar">{initials(staff.name)}</div>
                    <div className="flex-grow-1" style={{ minWidth: 220 }}>
                        <div className="d-flex align-items-center flex-wrap gap-2">
                            <h4 className="fw-semibold mb-0">{staff.name}</h4>
                            <span className={`badge rounded-pill text-capitalize ${EMPLOYMENT_STATUS_BADGE[staff.employmentStatus] || "text-bg-secondary"}`}>{staff.employmentStatus}</span>
                        </div>
                        <div className="text-muted small mb-2">{staff.employeeId}</div>
                        <div className="d-flex flex-wrap gap-2">
                            <Chip>{staff.designation || "Designation not set"}</Chip>
                            <Chip>{staff.roleName}</Chip>
                            <Chip>{staff.departmentName}</Chip>
                            {staff.hubName ? <Chip>{staff.hubName}</Chip> : null}
                            {staff.employmentType ? <Chip>{textCapitalize(staff.employmentType)}</Chip> : null}
                        </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2 staff-header-actions">
                        {isTerminated ? (
                            <span className="text-muted small align-self-center">Terminated staff can't be reactivated here.</span>
                        ) : (
                            <div className="dropdown">
                                <button type="button" className="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown" disabled={statusUpdating}>
                                    {statusUpdating ? "Updating..." : "Change status"}
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end">
                                    <li><button type="button" className="dropdown-item" onClick={() => changeStatus("active")}>Active</button></li>
                                    <li><button type="button" className="dropdown-item" onClick={() => changeStatus("suspended")}>Suspended</button></li>
                                    <li><button type="button" className="dropdown-item text-danger" onClick={() => changeStatus("terminated")}>Terminated</button></li>
                                </ul>
                            </div>
                        )}
                        <button type="button" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1" onClick={() => setShowRoleModal(true)}>
                            <BsPersonBadge /> Reassign role
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1" onClick={() => setShowPasswordModal(true)}>
                            <BsKey /> Reset password
                        </button>
                        <button type="button" className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1" onClick={() => navigate(`/staff/${staffId}/edit`)}>
                            <BsPencilSquare /> Edit
                        </button>
                    </div>
                </div>
            </div>

            {/* Quick stats strip - tenure/status/type/hub at a glance, reusing
                the same .stat-card look the dashboard already uses elsewhere. */}
            <div className="row g-3 mb-4">
                <div className="col-6 col-md-3">
                    <div className="stat-card h-100">
                        <div className="stat-number">{tenure || "-"}</div>
                        <div className="stat-label">Tenure</div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="stat-card h-100">
                        <div className="stat-number">{formatDate(staff.dateOfJoining) || "-"}</div>
                        <div className="stat-label">Joined</div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="stat-card h-100">
                        <div className="stat-number text-capitalize">{staff.employmentType ? textCapitalize(staff.employmentType) : "-"}</div>
                        <div className="stat-label">Employment type</div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="stat-card h-100">
                        <div className="stat-number">{staff.hubName || "-"}</div>
                        <div className="stat-label">Hub</div>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                <div className="col-12 col-lg-7">
                    <DetailCard title="Employment" icon={<BsBriefcase />}>
                        <InfoSection icon={<BsBriefcase />} title="">
                            <InfoItem label="Designation" value={staff.designation} />
                            <InfoItem label="Role" value={staff.roleName} />
                            <InfoItem label="Department" value={staff.departmentName} />
                            <InfoItem label="Hub" value={staff.hubName} />
                            <InfoItem label="Employment type" value={staff.employmentType ? textCapitalize(staff.employmentType) : null} />
                            <InfoItem label="Date of joining" value={formatDate(staff.dateOfJoining)} />
                            <InfoItem label="Probation end" value={staff.probationEndDate ? formatDate(staff.probationEndDate) : null} />
                            <InfoItem label="Reports to" value={staff.reportingTo} />
                        </InfoSection>
                    </DetailCard>

                    <StaffLocationCard staff={staff} onChanged={(next) => setStaff((current) => ({ ...current, locationTracking: next }))} />

                    <DetailCard title="Contact & Personal" icon={<BsTelephone />}>
                        <InfoSection>
                            <InfoItem label="Phone" value={staff.phone} />
                            <InfoItem label="Alternate phone" value={staff.alternatePhone} />
                            <InfoItem label="Email" value={staff.email} />
                            <InfoItem label="Date of birth" value={staff.dateOfBirth ? formatDate(staff.dateOfBirth) : null} />
                            <InfoItem label="Gender" value={textCapitalize(staff.gender)} />
                            <InfoItem label="Blood group" value={staff.bloodGroup} />
                            <InfoItem label="Marital status" value={staff.maritalStatus ? textCapitalize(staff.maritalStatus) : null} />
                        </InfoSection>
                        {hasAddress ? (
                            <InfoSection icon={<BsGeoAlt />} title="Address">
                                <InfoItem label="Current address" value={formatAddress(staff.currentAddress)} />
                                <InfoItem label="Permanent address" value={formatAddress(staff.permanentAddress)} />
                            </InfoSection>
                        ) : null}
                    </DetailCard>

                    {hasCompliance || hasEducation ? (
                        <DetailCard title="Documents & Education" icon={<BsFileEarmarkText />}>
                            {hasCompliance ? (
                                <InfoSection title="Identity documents">
                                    <InfoItem label="Aadhaar number" value={staff.aadhaarNumber} />
                                    <InfoItem label="PAN number" value={staff.panNumber} />
                                    <InfoItem label="Driving licence" value={staff.drivingLicenseNumber} />
                                    <InfoItem label="DL expiry" value={staff.drivingLicenseExpiry ? formatDate(staff.drivingLicenseExpiry) : null} />
                                </InfoSection>
                            ) : null}
                            {hasEducation ? (
                                <InfoSection title="Education">
                                    <InfoItem label="Highest education" value={staff.highestEducation} />
                                    <InfoItem label="Certifications" value={staff.certifications?.length ? staff.certifications.join(", ") : null} />
                                    <InfoItem label="Languages known" value={staff.languagesKnown?.length ? staff.languagesKnown.join(", ") : null} />
                                </InfoSection>
                            ) : null}
                        </DetailCard>
                    ) : null}

                    {hasFinancialInfo ? (
                        <DetailCard title="Financial" icon={<BsCashCoin />}>
                            <InfoSection>
                                <InfoItem label="Base salary" value={staff.baseSalary !== undefined ? currencyFormatter(staff.baseSalary) : null} />
                                <InfoItem label="Allowances" value={staff.allowances !== undefined ? currencyFormatter(staff.allowances) : null} />
                                <InfoItem label="Bank account" value={staff.bankDetails?.accountNumber} />
                                <InfoItem label="Bank name" value={staff.bankDetails?.bankName} />
                                <InfoItem label="Account holder" value={staff.bankDetails?.bankHolder} />
                                <InfoItem label="IFSC" value={staff.bankDetails?.ifsc} />
                                <InfoItem label="UAN number" value={staff.uanNumber} />
                                <InfoItem label="PF number" value={staff.pfNumber} />
                                <InfoItem label="ESI number" value={staff.esiNumber} />
                            </InfoSection>
                        </DetailCard>
                    ) : ""}

                    {hasEmergencyContact ? (
                        <DetailCard title="Emergency Contact" icon={<BsHeartPulse />}>
                            <InfoSection>
                                <InfoItem label="Name" value={staff.emergencyContact.name} />
                                <InfoItem label="Relationship" value={staff.emergencyContact.relationship} />
                                <InfoItem label="Phone" value={staff.emergencyContact.phone} />
                                <InfoItem label="Email" value={staff.emergencyContact.email} />
                            </InfoSection>
                        </DetailCard>
                    ) : ""}

                    <div className="text-muted small px-2 d-flex flex-wrap gap-3">
                        <span>Created {formatDateTime(staff.createdAt)}</span>
                        <span>Last updated {formatDateTime(staff.updatedAt)}</span>
                    </div>
                </div>

                <div className="col-12 col-lg-5">
                    {/* <DetailCard title="Activity Log" icon={<BsClockHistory />}>
                        {logs.length ? (
                            <ul className="hub-log-list">
                                {logs.map((log) => (
                                    <li key={log._id || `${log.createdAt}-${log.action}`} className="hub-log-item">
                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                            <span className={`badge rounded-pill ${log.action === "Write" ? "text-bg-primary" : "text-bg-secondary"}`}>{log.action}</span>
                                            <span className="text-muted small">{formatDateTime(log.createdAt)}</span>
                                        </div>
                                        <div className="small mt-1">{log?.previousData?.msg || "-"}</div>
                                        {log?.previousData?.data && Object.keys(log.previousData.data).length ? (
                                            <div className="hub-log-diff">
                                                {Object.entries(log.previousData.data).map(([field, value]) => (
                                                    <div key={field}>
                                                        <span className="text-muted">{field}:</span> was {formatPreviousValue(value)}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : ""}
                                        <div className="text-muted small mt-1">By {log.userId}</div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-muted mb-0">No activity recorded yet.</p>
                        )}
                    </DetailCard> */}
                    <Timeline userLog={logs}/>
                </div>
            </div>

            {showRoleModal ? (
                <StaffRoleModal
                    staffId={staffId}
                    currentDepartmentId={roleDepartmentId}
                    currentRoleId={staff.roleId}
                    departments={departments}
                    onClose={() => setShowRoleModal(false)}
                    onSaved={() => { setShowRoleModal(false); load(); }}
                />
            ) : ""}

            {showPasswordModal ? (
                <StaffPasswordModal
                    staffId={staffId}
                    onClose={() => setShowPasswordModal(false)}
                    onSaved={() => setShowPasswordModal(false)}
                />
            ) : ""}
        </>
    );
};

export default StaffDetail;
